import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { PurchaseRequestItem } from '../../purchase-request/models/purchase-request-item';
import {
  SkuAutocompleteService,
  SKUAutocompleteItem,
  SKUAutocompleteResponse,
} from '../../purchase-request/services/sku-autocomplete.service';
import { VendorSkuControllerService } from '@app/api/vendor-service/api/api';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@app/shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
  SearchFormData,
} from '@app/shared/components/search-form/search-form.component';

@Component({
  selector: 'app-purchase-request-item-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    AutoCompleteModule,
    TableModule,
    ToastModule,
    TooltipModule,
    AppButtonComponent,
    DataTableComponent,
    SearchFormComponent,
  ],
  providers: [MessageService],
  templateUrl: './selected-sku-dialog.component.html',
  styleUrl: './selected-sku-dialog.component.css',
})
export class PurchaseRequestItemDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() initialData: PurchaseRequestItem | null = null;
  @Input() editMode = false;
  @Input() existingItems: PurchaseRequestItem[] = [];
  @Input() vendorCode?: string;
  @Input() vendorSkus?: any[];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<PurchaseRequestItem>();

  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly skuAutocompleteService = inject(SkuAutocompleteService);
  private readonly vendorSkuService = inject(VendorSkuControllerService);

  itemForm!: FormGroup;
  loading = signal(false);

  skuItems: SKUAutocompleteItem[] = [];
  selectedSkus: SKUAutocompleteItem[] = [];
  totalRecords = 0;
  searchTerm = '';
  currentSearchParams: { skuCode: string; skuName: string } = {
    skuCode: '',
    skuName: '',
  };
  vendorSkuCodes: Set<string> = new Set();
  vendorSkuCodeMap: Map<string, string> = new Map();

  alreadyAddedSkuCodes: Set<string> = new Set();
  alreadyAddedSkus: SKUAutocompleteItem[] = [];

  searchFormat: FormFieldConfig[] = [];

  get tableColumns(): TableColumn[] {
    const baseColumns: TableColumn[] = [
      { label: 'purchase_request.fields.sku_code', prop: 'skuCode', type: 'text', width: 150 },
      { label: 'purchase_request.fields.sku_name', prop: 'skuName', type: 'text', width: 200 },
    ];
    const isPOMode = this.vendorSkus && this.vendorSkus.length > 0;
    if (isPOMode) {
      return [
        ...baseColumns,
        { label: 'purchase_request.fields.vendor_sku_code', prop: 'vendorSkuCode', type: 'text', width: 150 },
        { label: 'purchase_request.fields.vendor_product_name', prop: 'vendorProductName', type: 'text', width: 200 },
      ];
    }
    return [
      ...baseColumns,
      { label: 'purchase_request.fields.spu_code', prop: 'spuCode', type: 'text', width: 150 },
      { label: 'purchase_request.fields.spu_name', prop: 'spuName', type: 'text', width: 200 },
    ];
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeSearchFormat();
    this.initializeAlreadyAddedSkus();
  }

  ngOnChanges(changes: any): void {
    if (changes['vendorCode'] && !this.vendorCode) {
      this.vendorSkuCodes.clear();
      this.vendorSkuCodeMap.clear();
    }

    if (this.visible && this.itemForm) {
      this.initializeAlreadyAddedSkus();
      this.refreshSkuItemsWithDisableStatus();

      if (changes['visible'] && this.visible) {
        this.renderVendorSkuData();
      } else if (changes['vendorSkus'] || changes['vendorCode']) {
        this.renderVendorSkuData();
      }

      if (this.initialData && this.editMode) {
        this.populateForm();
      } else if (!this.editMode) {
        this.resetForm();
      }
    } else if (this.visible && !this.itemForm) {
      this.initializeForm();
      this.initializeAlreadyAddedSkus();
      this.renderVendorSkuData();
    }
  }

  private initializeForm(): void {
    this.itemForm = this.fb.group({
      spuCode: [''],
      spuName: [''],
      skuCode: ['', [Validators.required, Validators.maxLength(50)]],
      skuName: ['', [Validators.required, Validators.maxLength(200)]],
      quantity: [null, [Validators.required, Validators.min(1)]],
    });
  }

  private initializeSearchFormat(): void {
    this.searchFormat = [
      {
        key: 'skuCode',
        label: this.translateService.instant('purchase_request.search.sku_code'),
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.search.sku_code_placeholder'
        ),
      },
      {
        key: 'skuName',
        label: this.translateService.instant('purchase_request.search.sku_name'),
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.search.sku_name_placeholder'
        ),
      },
    ];
  }

  private initializeAlreadyAddedSkus(): void {
    this.alreadyAddedSkuCodes.clear();
    this.alreadyAddedSkus = [];
    this.existingItems.forEach((item) => {
      this.alreadyAddedSkuCodes.add(item.skuCode);
    });
  }

  private refreshSkuItemsWithDisableStatus(): void {
    if (this.skuItems && this.skuItems.length > 0) {
      this.skuItems = this.skuItems.map((item: any) => ({
        ...item,
        disable: this.isSkuAlreadyAdded(item.skuCode) ? 1 : 0,
      }));
    }
    this.clearSelectionForRemovedItems();
  }

  private clearSelectionForRemovedItems(): void {
    if (this.selectedSkus && this.selectedSkus.length > 0) {
      this.selectedSkus = this.selectedSkus.filter((selectedSku) =>
        this.existingItems.some(
          (existingItem) => existingItem.skuCode === selectedSku.skuCode
        )
      );
    }
  }

  private populateForm(): void {
    if (this.initialData) {
      this.itemForm.patchValue({
        spuCode: this.initialData.spuCode,
        spuName: this.initialData.spuName,
        skuCode: this.initialData.skuCode,
        skuName: this.initialData.skuName,
        quantity: this.initialData.quantity,
      });
      if (this.editMode) {
        this.itemForm.get('spuCode')?.disable();
        this.itemForm.get('spuName')?.disable();
      }
    }
  }

  private resetForm(): void {
    this.itemForm.reset();
    this.itemForm.patchValue({ quantity: null });
    this.itemForm.get('spuCode')?.enable();
    this.itemForm.get('spuName')?.enable();
    this.selectedSkus = [];
  }

  onAdd(): void {
    const requiredFieldsValid =
      this.itemForm.get('skuCode')?.valid &&
      this.itemForm.get('skuName')?.valid &&
      this.itemForm.get('quantity')?.valid &&
      this.itemForm.get('targetWarehouse')?.valid;
    const formData = this.itemForm.getRawValue();
    const spuFieldsValid =
      formData.spuCode &&
      formData.spuName &&
      formData.spuCode.trim() !== '' &&
      formData.spuName.trim() !== '';

    if (requiredFieldsValid && spuFieldsValid) {
      const item: PurchaseRequestItem = {
        id: this.initialData?.id || this.generateId(),
        spuCode: formData.spuCode,
        spuName: formData.spuName,
        skuCode: formData.skuCode,
        skuName: formData.skuName,
        quantity: formData.quantity,
      };
      const allExistingSkus = [...this.existingItems];
      if (!this.isSkuCodeUniqueInList(item.skuCode, item.id, allExistingSkus)) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'purchase_request.messages.validation_error'
          ),
          detail: this.translateService.instant(
            'purchase_request.messages.sku_code_already_exists',
            { skuCode: item.skuCode }
          ),
        });
        return;
      }
      this.loading.set(true);
      setTimeout(() => {
        this.loading.set(false);
        this.confirm.emit(item);
        this.resetForm();
      }, 500);
    } else {
      this.markFormGroupTouched();
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'purchase_request.messages.validation_error'
        ),
        detail: this.translateService.instant(
          'purchase_request.messages.please_fill_required_fields'
        ),
      });
    }
  }

  onAddAndClose(): void {
    const requiredFieldsValid =
      this.itemForm.get('skuCode')?.valid &&
      this.itemForm.get('skuName')?.valid &&
      this.itemForm.get('quantity')?.valid &&
      this.itemForm.get('targetWarehouse')?.valid;
    const formData = this.itemForm.getRawValue();
    const spuFieldsValid =
      formData.spuCode &&
      formData.spuName &&
      formData.spuCode.trim() !== '' &&
      formData.spuName.trim() !== '';

    if (requiredFieldsValid && spuFieldsValid) {
      const item: PurchaseRequestItem = {
        id: this.initialData?.id || this.generateId(),
        spuCode: formData.spuCode,
        spuName: formData.spuName,
        skuCode: formData.skuCode,
        skuName: formData.skuName,
        quantity: formData.quantity,
      };
      const allExistingSkus = [...this.existingItems];
      if (!this.isSkuCodeUniqueInList(item.skuCode, item.id, allExistingSkus)) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'purchase_request.messages.validation_error'
          ),
          detail: this.translateService.instant(
            'purchase_request.messages.sku_code_already_exists',
            { skuCode: item.skuCode }
          ),
        });
        return;
      }
      this.loading.set(true);
      setTimeout(() => {
        this.loading.set(false);
        this.confirm.emit(item);
        this.onCancel();
      }, 500);
    } else {
      this.markFormGroupTouched();
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'purchase_request.messages.validation_error'
        ),
        detail: this.translateService.instant(
          'purchase_request.messages.please_fill_required_fields'
        ),
      });
    }
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  renderVendorSkuData(page: number = 0, size: number = 20): void {
    const isPOMode = this.vendorSkus && this.vendorSkus.length > 0;
    if (isPOMode) {
      this.renderVendorSkusFromInput();
      return;
    }
    this.loadAllSystemSkus(page, size);
  }

  private renderVendorSkusFromInput(): void {
    if (!this.vendorSkus || this.vendorSkus.length === 0) {
      this.skuItems = [];
      this.totalRecords = 0;
      return;
    }
    let filteredSkus = [...this.vendorSkus];
    const { skuCode, skuName } = this.currentSearchParams;
    if (skuCode) {
      filteredSkus = filteredSkus.filter((sku: any) =>
        sku.skuCode?.toLowerCase().includes(skuCode.toLowerCase())
      );
    }
    if (skuName) {
      filteredSkus = filteredSkus.filter((sku: any) =>
        sku.skuName?.toLowerCase().includes(skuName.toLowerCase()) ||
        sku.vendorProductName?.toLowerCase().includes(skuName.toLowerCase())
      );
    }
    this.skuItems = filteredSkus.map((vendorSku: any) => ({
      id: vendorSku.id || 0,
      skuCode: vendorSku.skuCode || '',
      skuName: vendorSku.skuName || null,
      spuCode: '',
      spuName: '',
      materialName: '',
      categoryName: '',
      status: vendorSku.active === false ? 0 : 1,
      costPrice: 0,
      salesPrice: 0,
      marketPrice: 0,
      distributionPrice: 0,
      wholesalePrice: 0,
      clearingPrice: 0,
      weight: 0,
      length: 0,
      width: 0,
      height: 0,
      vendorSkuCode: vendorSku.vendorSkuCode || '',
      vendorProductName: vendorSku.vendorProductName || '',
      disable: this.isSkuAlreadyAdded(vendorSku.skuCode) ? 1 : 0,
    } as SKUAutocompleteItem & {
      vendorSkuCode?: string;
      vendorProductName?: string;
      disable?: number;
    }));
    filteredSkus.forEach((vendorSku: any) => {
      if (vendorSku.skuCode && vendorSku.vendorSkuCode) {
        this.vendorSkuCodeMap.set(vendorSku.skuCode, vendorSku.vendorSkuCode);
      }
    });
    this.totalRecords = filteredSkus.length;
  }

  private loadAllSystemSkus(page: number, size: number): void {
    this.loading.set(true);
    const { skuCode, skuName } = this.currentSearchParams;
    const searchObservable =
      skuCode && skuName
        ? this.skuAutocompleteService.searchBySkuCodeAndName(
            skuCode,
            skuName,
            page,
            size
          )
        : skuCode
          ? this.skuAutocompleteService.searchBySkuCode(skuCode, page, size)
          : skuName
            ? this.skuAutocompleteService.searchBySkuName(skuName, page, size)
            : this.skuAutocompleteService.getSkuItems(
                page,
                size,
                this.searchTerm
              );
    searchObservable.subscribe({
      next: (response: SKUAutocompleteResponse) => {
        this.skuItems = (response.pageContent || []).map((item: SKUAutocompleteItem) => ({
          ...item,
          vendorSkuCode: '',
          vendorProductName: '',
          disable: this.isSkuAlreadyAdded(item.skuCode) ? 1 : 0,
        }));
        this.totalRecords = response.totalElements || 0;
        this.loading.set(false);
      },
      error: () => {
        this.skuItems = [];
        this.totalRecords = 0;
        this.loading.set(false);
      },
    });
  }

  onLazyLoad(event: any): void {
    const page = event?.page ?? 0;
    const size = event?.rows ?? 20;
    this.renderVendorSkuData(page, size);
  }

  onSearch(searchData: SearchFormData): void {
    const skuCode = searchData['skuCode'] || '';
    const skuName = searchData['skuName'] || '';
    this.currentSearchParams = { skuCode, skuName };
    this.renderVendorSkuData(0, 20);
  }

  onSearchReset(): void {
    this.searchTerm = '';
    this.currentSearchParams = { skuCode: '', skuName: '' };
    this.renderVendorSkuData(0, 20);
  }

  getSelectedItems(): PurchaseRequestItem[] {
    const validSelectedSkus = this.selectedSkus.filter(
      (sku) => (sku as any).disable !== 1
    );
    return validSelectedSkus.map((sku) => ({
      id: this.generateId(),
      skuCode: sku.skuCode,
      skuName: sku.skuName ?? '',
      spuCode: sku.spuCode,
      spuName: sku.spuName,
      quantity: 1,
    }));
  }

  isSkuAlreadyAdded(skuCode: string): boolean {
    return this.alreadyAddedSkuCodes.has(skuCode);
  }

  getSkuSelectionState(skuItem: any): boolean {
    if (skuItem.disable === 1) return true;
    return this.selectedSkus.some(
      (selected) => selected.skuCode === skuItem.skuCode
    );
  }

  onConfirm(): void {
    const selectedItems = this.getSelectedItems();
    if (selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'purchase_request.messages.select_sku_items'
        ),
      });
      return;
    }
    const validItems = selectedItems.filter((item) => {
      const skuItem = this.skuItems.find((sku) => sku.skuCode === item.skuCode);
      return !skuItem || (skuItem as any).disable !== 1;
    });
    const duplicateItems = selectedItems.filter((item) => {
      const skuItem = this.skuItems.find((sku) => sku.skuCode === item.skuCode);
      return skuItem && (skuItem as any).disable === 1;
    });
    if (duplicateItems.length > 0 && validItems.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'purchase_request.messages.some_skus_already_added'
        ),
      });
    } else if (duplicateItems.length > 0 && validItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'purchase_request.messages.all_skus_already_added'
        ),
      });
      return;
    }
    validItems.forEach((item) => this.confirm.emit(item));
    this.onCancel();
  }

  private isSkuCodeUniqueInList(
    skuCode: string,
    currentItemId: string | undefined,
    skuList: PurchaseRequestItem[]
  ): boolean {
    if (!skuCode || skuList.length === 0) return true;
    const duplicateItem = skuList.find(
      (item) => item.skuCode === skuCode && item.id !== currentItemId
    );
    return !duplicateItem;
  }

  private markFormGroupTouched(): void {
    ['skuCode', 'skuName', 'quantity', 'targetWarehouse'].forEach((fieldName) => {
      this.itemForm.get(fieldName)?.markAsTouched();
    });
  }

  private generateId(): string {
    return (
      'item_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
    );
  }

  getFieldError(fieldName: string): string {
    const control = this.itemForm.get(fieldName);
    if (control?.errors && (control.touched || control.dirty)) {
      if (control.errors['required'])
        return `${this.getFieldLabel(fieldName)} is required`;
      if (control.errors['min'])
        return `${this.getFieldLabel(fieldName)} must be greater than ${control.errors['min'].min}`;
      if (control.errors['maxlength'])
        return this.translateService.instant('common.validation.maxlength', {
          fieldName: this.getFieldLabel(fieldName),
          requiredLength: control.errors['maxlength'].requiredLength,
        });
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      spuCode: 'SPU Code',
      spuName: 'SPU Name',
      skuCode: 'SKU Code',
      skuName: 'SKU Name',
      quantity: 'Quantity',
    };
    return labels[fieldName] || fieldName;
  }

  getDialogHeader(): string {
    return this.translateService.instant('purchase_request.dialog.select_skus');
  }

  isCurrentSkuCodeUnique(): boolean {
    const currentSkuCode = this.itemForm.get('skuCode')?.value;
    if (!currentSkuCode) return true;
    return this.isSkuCodeUniqueInList(
      currentSkuCode,
      this.initialData?.id,
      this.existingItems
    );
  }

  getSkuCodeUniquenessMessage(): string {
    if (!this.itemForm.get('skuCode')?.value) return '';
    if (!this.isCurrentSkuCodeUnique())
      return 'This SKU code is already used in the item details list';
    return '';
  }
}
